/**
 * Calendario laborable del proyecto (paridad con ProjectCalendarService / Project::DEFAULT_WORK_DAYS).
 */
import axios from 'axios'
import moment from 'moment'

/** Mismo default que App\Models\Project::DEFAULT_WORK_DAYS */
export const DEFAULT_WORK_DAYS = {
  sunday: false,
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: false,
}

const WEEKDAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
]

/** @type {Map<string, Set<string>>} */
const holidayCache = new Map()

/** @type {Map<string, Promise<Set<string>>>} */
const holidayInflight = new Map()

function cacheKey(projectId) {
  return String(projectId)
}

export function parseCalendarMoment(value) {
  if (!value) {
    return null
  }
  const parsed = moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD'], true)
  return parsed.isValid() ? parsed.startOf('day') : null
}

/** work_days del proyecto o default lun–vie si no está configurado. */
export function resolveWorkDays(configs) {
  const raw = configs && configs.work_days
  if (raw && typeof raw === 'object') {
    return raw
  }
  return DEFAULT_WORK_DAYS
}

/**
 * ¿Es laborable según weekday? (sin festivos).
 * Si work_days existe en configs pero falta un día → no laborable (como empty() en PHP).
 */
export function isWorkingWeekday(dateMoment, workDays) {
  const key = WEEKDAY_KEYS[dateMoment.day()]
  if (!key) {
    return false
  }

  const hasConfig = workDays && typeof workDays === 'object'
  if (!hasConfig) {
    return !!DEFAULT_WORK_DAYS[key]
  }
  if (Object.prototype.hasOwnProperty.call(workDays, key)) {
    return !!workDays[key]
  }
  return false
}

export function isWorkingDay(dateMoment, workDays, holidaySet) {
  if (!isWorkingWeekday(dateMoment, workDays)) {
    return false
  }
  const holidays = holidaySet instanceof Set ? holidaySet : new Set()
  return !holidays.has(dateMoment.format('YYYY-MM-DD'))
}

/**
 * Días laborables inclusive entre dos fechas (intercambia si vienen invertidas).
 * Paridad con ProjectCalendarService::workingDaysBetween().
 */
export function workingDaysBetween(startDate, endDate, workDays, holidaySet = new Set()) {
  let start = parseCalendarMoment(startDate)
  let end = parseCalendarMoment(endDate)
  if (!start || !end) {
    return 0
  }

  if (start.isAfter(end)) {
    const tmp = start
    start = end
    end = tmp
  }

  let count = 0
  const cursor = start.clone()
  const holidays = holidaySet instanceof Set ? holidaySet : new Set()

  while (cursor.isSameOrBefore(end, 'day')) {
    if (isWorkingDay(cursor, workDays, holidays)) {
      count += 1
    }
    cursor.add(1, 'day')
  }

  return count
}

/** Paridad con ProjectCalendarService::activityDurationInWorkingDays() → mínimo 1. */
export function activityDurationInWorkingDays(startDate, endDate, workDays, holidaySet = new Set()) {
  return Math.max(1, workingDaysBetween(startDate, endDate, workDays, holidaySet))
}

/**
 * Festivos del proyecto (calendar_holidays.date_day), cacheados por projectId.
 * Deduplica requests en vuelo; no cachea fallos de red/API.
 */
export async function fetchProjectHolidayDates(projectId) {
  if (!projectId) {
    return new Set()
  }

  const key = cacheKey(projectId)
  if (holidayCache.has(key)) {
    return holidayCache.get(key)
  }
  if (holidayInflight.has(key)) {
    return holidayInflight.get(key)
  }

  const request = axios
    .get(`/api/v2/project/${projectId}/calendar`)
    .then(({ data }) => {
      const holidays = (data && data.data && data.data.holidays) || []
      const set = new Set()
      holidays.forEach((holiday) => {
        const parsed = parseCalendarMoment(holiday.date_day || holiday.date)
        if (parsed) {
          set.add(parsed.format('YYYY-MM-DD'))
        }
      })
      holidayCache.set(key, set)
      return set
    })
    .catch(() => new Set())
    .finally(() => {
      holidayInflight.delete(key)
    })

  holidayInflight.set(key, request)
  return request
}

export function invalidateProjectHolidayCache(projectId) {
  if (!projectId) {
    return
  }
  const key = cacheKey(projectId)
  holidayCache.delete(key)
  holidayInflight.delete(key)
}
